import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SpotifyCoopQuiz } from './SpotifyCoopQuiz';

interface QuizTrack {
  id: string;
  name: string;
  artist: string;
  albumImage: string;
  owner: 'mauricio' | 'novia';
  ownerName: string;
}

interface QuizMusicalProps {
  myTracks: any[];
  herTracks: any[];
  myProfile: any;
  herProfile: any;
}

export const QuizMusical: React.FC<QuizMusicalProps> = ({ 
  myTracks, 
  herTracks, 
  myProfile, 
  herProfile 
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState({ mauricio: 0, novia: 0 });
  const [gameStarted, setGameStarted] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [quizTracks, setQuizTracks] = useState<QuizTrack[]>([]);
  const [showCoopQuiz, setShowCoopQuiz] = useState(false);

  // Generar preguntas del quiz
  const generateQuiz = () => {
    const mauricioTracks = myTracks.map(track => ({
      id: track.id,
      name: track.name,
      artist: track.artists[0].name,
      albumImage: track.album.images[0]?.url,
      owner: 'mauricio' as const,
      ownerName: myProfile?.display_name || 'Mauricio'
    }));

    const noviaTracks = herTracks.map(track => ({
      id: track.id,
      name: track.name,
      artist: track.artists[0].name,
      albumImage: track.album.images[0]?.url,
      owner: 'novia' as const,
      ownerName: herProfile?.display_name || 'Tu Novia'
    }));

    // Find shared songs (same ID or same name + artist)
    const sharedTracks: QuizTrack[] = [];
    mauricioTracks.forEach(mTrack => {
      const matching = noviaTracks.find(nTrack =>
        mTrack.id === nTrack.id ||
        (mTrack.name.toLowerCase() === nTrack.name.toLowerCase() &&
         mTrack.artist.toLowerCase() === nTrack.artist.toLowerCase())
      );
      if (matching) {
        // Randomly assign owner for shared tracks
        const randomOwner = Math.random() > 0.5 ? mTrack : matching;
        sharedTracks.push(randomOwner);
      }
    });

    // Get unique tracks from each person
    const uniqueMauricioTracks = mauricioTracks.filter(mTrack =>
      !sharedTracks.some(sTrack =>
        sTrack.id === mTrack.id ||
        (sTrack.name.toLowerCase() === mTrack.name.toLowerCase() &&
         sTrack.artist.toLowerCase() === mTrack.artist.toLowerCase())
      )
    );

    const uniqueNoviaTracks = noviaTracks.filter(nTrack =>
      !sharedTracks.some(sTrack =>
        sTrack.id === nTrack.id ||
        (sTrack.name.toLowerCase() === nTrack.name.toLowerCase() &&
         sTrack.artist.toLowerCase() === nTrack.artist.toLowerCase())
      )
    );

    // Prioritize shared songs (4-5 questions), mix in random ones (2-3 questions)
    const shuffledShared = sharedTracks.sort(() => Math.random() - 0.5);
    const shuffledUnique = [...uniqueMauricioTracks, ...uniqueNoviaTracks].sort(() => Math.random() - 0.5);

    // Take 4-5 shared songs and 2-3 random songs to make 7 total
    const sharedCount = Math.min(5, sharedTracks.length);
    const randomCount = 7 - sharedCount;

    const finalQuiz = [
      ...shuffledShared.slice(0, sharedCount),
      ...shuffledUnique.slice(0, randomCount)
    ].sort(() => Math.random() - 0.5); // Shuffle the final quiz

    setQuizTracks(finalQuiz.slice(0, 7));
  };

  const startGame = () => {
    generateQuiz();
    setGameStarted(true);
    setCurrentQuestion(0);
    setScore({ mauricio: 0, novia: 0 });
    setGameFinished(false);
  };

  const handleAnswer = (answer: string) => {
    const correct = quizTracks[currentQuestion];
    setSelectedAnswer(answer);
    setShowResult(true);

    // Actualizar puntaje
    if (answer === correct.owner) {
      if (correct.owner === 'mauricio') {
        setScore(prev => ({ ...prev, mauricio: prev.mauricio + 1 }));
      } else {
        setScore(prev => ({ ...prev, novia: prev.novia + 1 }));
      }
    }

    // Siguiente pregunta después de 2 segundos
    setTimeout(() => {
      if (currentQuestion < quizTracks.length - 1) {
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setShowResult(false);
      } else {
        setGameFinished(true);
      }
    }, 2000);
  };

  const resetGame = () => {
    setGameStarted(false);
    setGameFinished(false);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
  };

  if (!gameStarted) {
    return (
      <>
        <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 p-6 rounded-lg border border-purple-500/30">
          <div className="text-center">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-2xl font-bold text-purple-400 mb-4">Quiz Musical</h3>
            <p className="text-gray-400 mt-20 my-6">
              ¿De quién es esta canción? ¡Pon a prueba qué tanto conocen sus gustos musicales!
            </p>
            <div className="flex flex-col gap-3 items-center">
              <button
                onClick={startGame}
                disabled={myTracks.length === 0 || herTracks.length === 0}
                className="px-6 py-3 bg-purple-500 text-white rounded-full font-bold hover:bg-purple-600 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {myTracks.length === 0 || herTracks.length === 0 ?
                  'Necesitan datos de ambos para jugar' :
                  '🎮 Comenzar Quiz'
                }
              </button>
              <button
                onClick={() => setShowCoopQuiz(true)}
                disabled={myTracks.length === 0 || herTracks.length === 0}
                className="px-6 py-3 bg-pink-500/20 text-pink-400 border border-pink-500/50 rounded-full font-bold hover:bg-pink-500/30 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                👥 Modo Co-op
              </button>
            </div>
          </div>
        </div>

        {/* Co-op Quiz Modal */}
        {showCoopQuiz && (
          <SpotifyCoopQuiz
            myTracks={myTracks}
            herTracks={herTracks}
            myProfile={myProfile}
            herProfile={herProfile}
            onClose={() => setShowCoopQuiz(false)}
          />
        )}
      </>
    );
  }

  if (gameFinished) {
    const winner = score.mauricio > score.novia ? 'mauricio' : 
                  score.novia > score.mauricio ? 'novia' : 'empate';
    
    return (
      <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 p-6 rounded-lg border border-purple-500/30">
        <div className="text-center">
          <div className="text-6xl mb-4">
            {winner === 'empate' ? '🤝' : winner === 'mauricio' ? '🎉' : '💕'}
          </div>
          <h3 className="text-2xl font-bold text-purple-400 mb-4">¡Quiz Completado!</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${winner === 'mauricio' ? 'bg-green-500/20 border border-green-500' : 'bg-white/5'}`}>
              <div className="text-lg font-bold text-green-400">
                {myProfile?.display_name || 'Mauricio'}
              </div>
              <div className="text-3xl font-bold">{score.mauricio}</div>
            </div>
            <div className={`p-4 rounded-lg ${winner === 'novia' ? 'bg-pink-500/20 border border-pink-500' : 'bg-white/5'}`}>
              <div className="text-lg font-bold text-pink-400">
                {herProfile?.display_name || 'Tu Novia'}
              </div>
              <div className="text-3xl font-bold">{score.novia}</div>
            </div>
          </div>

          <p className="text-gray-400 mb-6">
            {winner === 'empate' ? 
              '¡Empate! Conocen igual de bien sus gustos musicales 🎵' :
              winner === 'mauricio' ?
              '¡Mauricio conoce mejor los gustos musicales! 🎯' :
              '¡Tu novia conoce mejor los gustos musicales! 💕'
            }
          </p>

          <button
            onClick={resetGame}
            className="px-6 py-3 bg-purple-500 text-white rounded-full font-bold hover:bg-purple-600 transition-all hover:scale-105"
          >
            🔄 Jugar Otra Vez
          </button>
        </div>
      </div>
    );
  }

  const track = quizTracks[currentQuestion];
  
  return (
    <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 p-6 rounded-lg border border-purple-500/30">
      <div className="text-center mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-400">
            Pregunta {currentQuestion + 1} de {quizTracks.length}
          </div>
          <div className="flex gap-4 text-sm">
            <span className="text-green-400">
              {myProfile?.display_name || 'Mauricio'}: {score.mauricio}
            </span>
            <span className="text-pink-400">
              {herProfile?.display_name || 'Tu Novia'}: {score.novia}
            </span>
          </div>
        </div>

        <h3 className="text-xl font-bold text-purple-400 mb-6">
          ¿De quién es esta canción?
        </h3>

        <motion.div 
          key={currentQuestion}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/5 p-6 rounded-lg mb-6"
        >
          <img 
            src={track.albumImage || 'https://via.placeholder.com/200x200/1DB954/white?text=♪'}
            alt={track.name}
            className="w-32 h-32 mx-auto rounded-lg mb-4"
          />
          <h4 className="text-xl font-bold text-white mb-2">{track.name}</h4>
          <p className="text-gray-400">{track.artist}</p>
        </motion.div>

        {!showResult ? (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleAnswer('mauricio')}
              className="p-4 bg-green-500/20 border border-green-500 rounded-lg hover:bg-green-500/30 transition-all"
            >
              <div className="text-lg font-bold text-green-400">
                {myProfile?.display_name || 'Mauricio'}
              </div>
            </button>
            <button
              onClick={() => handleAnswer('novia')}
              className="p-4 bg-pink-500/20 border border-pink-500 rounded-lg hover:bg-pink-500/30 transition-all"
            >
              <div className="text-lg font-bold text-pink-400">
                {herProfile?.display_name || 'Tu Novia'}
              </div>
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="text-4xl mb-2">
              {selectedAnswer === track.owner ? '✅' : '❌'}
            </div>
            <p className="text-lg">
              {selectedAnswer === track.owner ? '¡Correcto!' : 'Incorrecto'}
            </p>
            <p className="text-gray-400">
              Es de <span className="font-bold">{track.ownerName}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};